import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, Flex, Text } from '@mantine/core';
import React, { useState } from 'react';
import { useLinkedProjectsTable } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/LinkedProjectsTable/useLinkedProjectsTable.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { IconCircleCheck } from '@tabler/icons-react';
import { GetGeneratableDocumentsTemplateAssignedProjectRes } from '@dealstack/shared/lib/generated';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { createLinkedProjectsTableColumns } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/LinkedProjectsTable/columns.tsx';
import { LinkNewProjectDrawer } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/LinkedProjectsTable/LinkNewProjectDrawer.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { ProjectCustomizationDrawer } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/LinkedProjectsTable/ProjectCustomizationDrawer.tsx';

interface Props {
	templateId: string;
}

export const LinkedProjectsTable = ({ templateId }: Props) => {
	const {
		availableProjects,
		isLoading,
		linkedProjects,
		isAllProjectsLinked,
		refetchLinkedProjects,
		onUnassignProject,
	} = useLinkedProjectsTable(templateId);

	const mantineTableDataOperations = useMantineTableDataOperations();

	const [showLinkNewProjectModal, setShowLinkNewProjectModal] = useState(false);
	const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

	return (
		<Flex direction="column" gap="1rem">
			<Flex gap="1rem" align="center" justify="space-between" wrap="nowrap" w="100%">
				<Text size="B5Bold">Linked projects:</Text>

				{isAllProjectsLinked ? (
					<Flex
						gap="0.5rem"
						align="center"
						sx={{
							backgroundColor: defaultTheme.v3.colors.green[0],
							borderRadius: '0.5rem',
							padding: '0.5rem',
						}}
					>
						<IconCircleCheck color={defaultTheme.v3.colors.green[8]} />
						<Text size="B6Medium">All projects already linked</Text>
					</Flex>
				) : (
					<Button
						size="sm"
						onClick={() => setShowLinkNewProjectModal(true)}
						leftSection={<Icon variant="plus" size="lg" />}
					>
						Link project
					</Button>
				)}
			</Flex>
			<MantineTable<GetGeneratableDocumentsTemplateAssignedProjectRes>
				isLoading={isLoading}
				data={linkedProjects}
				columns={createLinkedProjectsTableColumns(setSelectedDealId, onUnassignProject)}
				rowCount={linkedProjects.length}
				dataOperations={mantineTableDataOperations}
				manualPagination={true}
				manualFiltering={false}
				manualSorting={false}
				enableRowSelection={false}
				renderBulkActionsMenu={false}
			/>
			<LinkNewProjectDrawer
				opened={showLinkNewProjectModal}
				onClose={() => setShowLinkNewProjectModal(false)}
				templateId={templateId}
				availableDeals={availableProjects}
				refetchLinkedProjects={refetchLinkedProjects}
			/>
			<ProjectCustomizationDrawer
				opened={!!selectedDealId}
				onClose={() => setSelectedDealId(null)}
				dealId={selectedDealId}
			/>
		</Flex>
	);
};
